import React, { useState, useCallback } from 'react';
import { Button, Icon, TextArea, TextInput, Select, RadioButton, withToaster } from '@gravity-ui/uikit';
import ChevronDown from '@gravity-ui/icons/ChevronDown';
import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import {
    lengthConfig,
    validateActivationPhrase,
    validateEmail,
    validateName,
    validateDeveloperName,
} from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import * as utils from 'client/blocks/Settings/channels/utils';
import {
    ApkFileUploaderInput,
    FileUploaderInput,
    AppIconUploader,
} from 'client/blocks-redesign/FilesUploaders/FilesUploaders';
import { OperationStatus } from 'client/blocks-redesign/statuses/OperationStatus/OperationStatus';
import { ControlPanel } from 'client/blocks-redesign/ControlPanel/ControlPanel';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { FormCard } from 'client/blocks-redesign/form/FormCard/FormCard';
import { IconMedium } from 'client/blocks-redesign/AppIcons/AppIcons';
import { InjectedFormProps } from 'client/hocs/types';
import { isIntegerNumber } from 'client/lib/utils';
import {
    getStringFieldErrorMessage,
    mapApiErrorsToFormErrors,
    mapInstallableAppSettingsToValues,
    mapValuesToInstallableAppSettings,
} from 'client/utils/settings/installable-app';
import formContainer, { FormContainerProps } from 'client/hocs/form-container';
import { ScreenshotsBlock } from 'client/blocks-redesign/form/ScreenShots/ScreenshotsBlock';
import { InstallableAppAccessField } from 'client/blocks-redesign/form/InstallableAppAccessField/InstallableAppAccessField';
import { InstallableApp, Channel } from 'client/model/skill';
import { InstallableAppFormSettings, AgeRestriction, AppVersionInfoWithTargetPlatforms } from 'client/types';
import { installableAppLabels as l } from 'client/blocks/Settings/channels/labels';
import { InstallableAppSettings } from 'client/model/settings';
import { TvAppTargetPlatformSelect } from 'client/blocks-redesign/form/TvAppTargetPlatformSelect/TvAppTargetPlatformSelect';
import { updateInstallableAppDraft, ApiError } from 'client/api';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { WithAppStoreSnapshot, withAppStoreSnapshot } from 'client/context/appStoreSnapshotContext';
import { ModeratorNotes } from 'client/blocks-redesign/statuses/ModeratorNotes/ModeratorNotes';
import { addToast } from 'client/utils/settings/toaster';
import * as keyset from 'i18n/InstallableAppForm';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as settingsLabelKeyset from 'i18n/SettingsLabels';
import * as TvAppIndexKeyset from 'i18n/InstallableAppsIndex';
import * as settingsErrorsKeyset from 'i18n/SettingsErrors';

import styles from './InstallableAppForm.module.scss';

// https://developer.android.com/studio/publish/versioning#versioningsettings
const MAX_VERSION_CODE = 2100000000;

interface OuterProps extends WithAppStoreSnapshot {
    skillUseZora: boolean;
    logoUrl: string;
    disabled: boolean;
    isDraft: boolean;
    handleFormSubmit?: (e?: React.FormEvent) => Promise<void>;
    settings: InstallableAppSettings;
    app: InstallableApp;
}

type Props = InjectedFormProps<InstallableAppFormSettings, OuterProps>;

const t = i18n(keyset);
const tGeneralSettings = i18n(generalSettingsKeyset);
const tSettingsLabel = i18n(settingsLabelKeyset);
const tTvAppIndex = i18n(TvAppIndexKeyset);
const tError = i18n(settingsErrorsKeyset);

const enum ApkErrors {
    NameError = 'nameError',
    VersionError = 'versionError',
    UploadApkError = 'uploadApkError',
    TargetPlatforms = 'targetPlatforms',
}

const InstallableAppSettingFields: React.SFC<Props> = (props) => {
    const {
        values,
        errors,
        isDraft,
        onChange,
        onUploaderChange,
        app,
        appStoreSnapshot,
        setStateAndValidate,
        handleFormSubmit,
        isNonValidationError,
    } = props;

    const [openCardName, setOpenCardName] = useState('');

    const appWasPublished = Boolean(app.firstPublishedAt);
    const categories = appStoreSnapshot.categories;
    const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.app.developerType);
    const isUserAdmin = __appConfig.user.isAdmin;

    const updateAppVersion = useCallback(
        (updatedValues: Partial<AppVersionInfoWithTargetPlatforms>) => {
            const newValue: Partial<AppVersionInfoWithTargetPlatforms> = {
                ...values.apkInfoWithTargets,
                ...updatedValues,
            };
            onChange('apkInfoWithTargets')(newValue);
        },
        [onChange, values.apkInfoWithTargets],
    );

    const handleMobileCardClick = (tabName: string) => {
        if (tabName === openCardName) {
            setOpenCardName('');
        } else {
            setOpenCardName(tabName);
        }
    };

    return (
        <ErrorBoundary withHomepageLink>
            <div>
                <div className={styles.header}>
                    <div className={styles.header__container}>
                        <div className={styles.appTitle}>
                            <IconMedium imageUrl={app.draft.logoUrl} />
                            <h1 className={styles.appTitle__header}>{app.onAir ? app.name : app.draft.name}</h1>
                            <div className={styles.appTitle__mobileStatusIcon}>
                                <OperationStatus isDraft={isDraft} />
                            </div>
                        </div>
                        <ControlPanel
                            handleSaveDraft={handleFormSubmit}
                            settingValues={values}
                            onSettingValuesChange={onChange}
                            settingFormErrors={errors}
                            isNonValidationError={isNonValidationError}
                        />
                    </div>
                    {isDraft && <ModeratorNotes />}
                </div>
                <div className={styles.mobileFormHeader}>
                    <ChevronDown />
                    {tTvAppIndex('main-information')}
                </div>
                <FormCard
                    cardKey="about"
                    cardTitle={t('about')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required
                        fieldTitle={t('app-name-title')}
                        fieldDescription={t('app-name-description')}
                        error={errors.name}
                    >
                        <TextInput
                            autoComplete={false}
                            className={styles.formElement}
                            disabled={props.disabled}
                            onChange={(e) => {
                                const newActivationPhrases = [e.target.value, ...values.activationPhrases.slice(1)];
                                setStateAndValidate(
                                    {
                                        values: {
                                            ...values,
                                            name: e.target.value,
                                            activationPhrases: newActivationPhrases,
                                        },
                                    },
                                    ['name', 'activationPhrases'],
                                );
                            }}
                            placeholder={t('app-name-placeholder')}
                            size="xl"
                            value={values.name}
                        />
                    </Field>
                    <Field fieldTitle={l.activationPhrase} fieldDescription={l.activationPhrases}>
                        {values.activationPhrases.map((phraseText, phraseIndex) => (
                            <TextInput
                                className={styles.formElement}
                                errorMessage={errors.activationPhrases?.[phraseIndex]}
                                key={phraseIndex}
                                onChange={(e) => {
                                    const copiedPhrases = [...values.activationPhrases];
                                    copiedPhrases[phraseIndex] = e.target.value;
                                    onChange('activationPhrases')(copiedPhrases);
                                }}
                                value={phraseText}
                                disabled={props.disabled || phraseIndex === 0}
                                rightContent={
                                    phraseIndex > 0 ? (
                                        <Button
                                            onClick={() => {
                                                const updatedPhrases = [...values.activationPhrases];
                                                updatedPhrases.splice(phraseIndex, 1);
                                                onChange('activationPhrases')(updatedPhrases);
                                            }}
                                            view="flat"
                                            disabled={props.disabled}
                                        >
                                            <Icon data={Xmark} />
                                        </Button>
                                    ) : undefined
                                }
                                size="xl"
                            />
                        ))}
                        <Button
                            className={styles.formButton}
                            onClick={() => onChange('activationPhrases')([...values.activationPhrases, ''])}
                            disabled={props.disabled || (!validateAsAdmin && values.activationPhrases?.length >= 4)}
                        >
                            <Icon data={Plus} />
                            {t('add')}
                        </Button>
                    </Field>
                    <Field
                        required
                        fieldTitle={t('short-description-title')}
                        fieldDescription={t('short-description-description')}
                        error={errors.secondaryTitle}
                    >
                        <TextInput
                            autoComplete={false}
                            className={styles.formElement}
                            disabled={props.disabled}
                            onChange={(e) => onChange('secondaryTitle')(e.target.value)}
                            placeholder={t('short-description-placeholder')}
                            size="xl"
                            value={values.secondaryTitle}
                        />
                    </Field>
                    <Field required fieldTitle={t('description-title')} fieldDescription={t('description-description')}>
                        <TextArea
                            className={styles.formElement}
                            disabled={props.disabled}
                            error={errors.description}
                            note={
                                <span className={styles.grayNote}>
                                    {lengthConfig.description - values.description?.length ?? lengthConfig.description}
                                </span>
                            }
                            rows={4}
                            size="xl"
                            onChange={(e) => onChange('description')(e.target.value)}
                            value={values.description}
                        />
                    </Field>
                    <Field fieldTitle={t('category-title')} required>
                        <Select
                            className={styles.formElement}
                            disabled={props.disabled}
                            onUpdate={(value) => {
                                onChange('category')(value[0]);
                            }}
                            options={categories.map(({ title }) => ({ value: title, content: title }))}
                            placeholder={t('category-placeholder')}
                            size="xl"
                            value={[values.category]}
                        />
                    </Field>
                    <Field
                        fieldTitle={t('age-restriction-title')}
                        fieldDescription={t('age-restriction-description')}
                        required
                        error={errors.ageRestriction}
                    >
                        <Select
                            className={styles.formElement}
                            disabled={props.disabled}
                            onUpdate={(value) => {
                                onChange('ageRestriction')(value[0] as AgeRestriction);
                            }}
                            options={appStoreSnapshot.ageRestrictions.map((value) => ({ value, content: value }))}
                            placeholder={t('age-restriction-placeholder')}
                            size="xl"
                            value={values.ageRestriction ? [values.ageRestriction] : undefined}
                        />
                    </Field>
                </FormCard>
                <FormCard
                    cardKey="application"
                    cardTitle={t('application')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required
                        fieldTitle={t('packet-name-title')}
                        fieldDescription={t('packet-name-description')}
                        error={errors.packageName}
                    >
                        <TextInput
                            // theme="normal"
                            className={styles.formElement}
                            disabled={props.disabled || appWasPublished}
                            onChange={(e) => onChange('packageName')(e.target.value)}
                            placeholder={t('packet-name-placeholder')}
                            size="xl"
                            value={values.packageName}
                        />
                    </Field>
                    <Field required fieldTitle={l.developerName} error={errors.developerName}>
                        <TextInput
                            className={styles.formElement}
                            disabled={props.disabled}
                            onChange={(e) => onChange('developerName')(e.target.value)}
                            size="xl"
                            value={values.developerName}
                        />
                    </Field>
                    <Field fieldTitle={l.email} fieldDescription={tSettingsLabel('description-email')}>
                        <TextInput
                            className={styles.formElement}
                            disabled={props.disabled}
                            error={errors.email}
                            onChange={(e) => onChange('email')(e.target.value)}
                            size="xl"
                            type="email"
                            value={values.email}
                        />
                    </Field>
                    <InstallableAppAccessField
                        installableAppAccess={values.skillAccess}
                        setInstallableAppAccess={onChange('skillAccess')}
                        disabled={props.disabled}
                        channel={Channel.TvApp}
                    />
                    {isUserAdmin && (
                        <Field fieldTitle={'isTrusted'}>
                            <RadioButton
                                className={styles.formButton}
                                disabled={props.disabled}
                                options={[
                                    { value: 'True', content: 'True' },
                                    { value: 'False', content: 'False' },
                                ]}
                                onUpdate={(value) => onChange('isTrusted')(value === 'True')}
                                value={values.isTrusted ? 'True' : 'False'}
                                size="xl"
                            />
                        </Field>
                    )}
                    <Field
                        fieldTitle={t('version-name-title')}
                        fieldDescription={t('version-name-description')}
                        required
                        error={''}
                    >
                        <TextInput
                            className={styles.formElement}
                            disabled={props.disabled}
                            error={errors.apkInfoWithTargets === ApkErrors.NameError && t('version-name-title')}
                            onChange={(e) => updateAppVersion({ versionName: e.target.value })}
                            placeholder={t('version-name-placeholder')}
                            size="xl"
                            value={values.apkInfoWithTargets.versionName}
                        />
                    </Field>
                    <Field
                        fieldTitle={t('version-title')}
                        fieldDescription={t('version-description')}
                        required
                        error={''}
                    >
                        <TextInput
                            className={styles.formElement}
                            disabled={props.disabled || !values.apkInfoWithTargets?.versionName}
                            error={
                                values.apkInfoWithTargets?.versionName &&
                                errors.apkInfoWithTargets === ApkErrors.VersionError &&
                                t('version-title')
                            }
                            onChange={(e) => {
                                if (e.target.value !== '' && isIntegerNumber(String(Number(e.target.value)))) {
                                    if (Number(e.target.value) > MAX_VERSION_CODE) return;
                                    updateAppVersion({
                                        versionCode: Number(e.target.value),
                                    });
                                } else {
                                    updateAppVersion({
                                        versionCode: undefined,
                                    });
                                }
                            }}
                            placeholder={t('version-placeholder')}
                            size="xl"
                            value={String(values.apkInfoWithTargets.versionCode ?? '')}
                        />
                    </Field>
                    <Field fieldTitle={l.versionNotes} error={''}>
                        <TextInput
                            className={styles.formElement}
                            size="xl"
                            value={values.apkInfoWithTargets.versionNotes}
                            onChange={(e) => updateAppVersion({ versionNotes: e.target.value })}
                            disabled={props.disabled}
                        />
                    </Field>
                    <Field required fieldTitle={t('apk-title')} fieldDescription={t('apk-description')}>
                        <ApkFileUploaderInput
                            disabled={
                                props.disabled ||
                                !values.apkInfoWithTargets?.versionName ||
                                !values.apkInfoWithTargets?.versionCode
                            }
                            updateAppVersion={updateAppVersion}
                            validationError={
                                errors.apkInfoWithTargets === ApkErrors.UploadApkError
                                    ? t('apk-description')
                                    : undefined
                            }
                            versionName={values?.apkInfoWithTargets?.versionName}
                            versionCode={values?.apkInfoWithTargets?.versionCode}
                            versionNotes={values?.apkInfoWithTargets?.versionNotes}
                        />
                    </Field>
                    {isUserAdmin && (
                        <TvAppTargetPlatformSelect
                            error={
                                errors.apkInfoWithTargets === ApkErrors.TargetPlatforms
                                    ? tError('apk-state')
                                    : undefined
                            }
                            disabled={props.disabled || !values.apkInfoWithTargets?.appVersionId}
                            availableTargetPlatforms={appStoreSnapshot.targetPlatformsByChannel?.[app.channel] || []}
                            selectedTargetPlatforms={values.apkInfoWithTargets.targetPlatforms}
                            updateSelectedTargetPlatforms={(platform: string, newValue: boolean) => {
                                const targetPlatforms = new Set(values.apkInfoWithTargets?.targetPlatforms ?? []);

                                if (newValue) {
                                    targetPlatforms.add(platform);
                                } else {
                                    targetPlatforms.delete(platform);
                                }

                                updateAppVersion({ targetPlatforms: Array.from(targetPlatforms.values()) });
                            }}
                        />
                    )}
                </FormCard>
                <FormCard
                    cardKey="assets"
                    cardTitle={t('resources')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field
                        required
                        fieldTitle={t('icon-title')}
                        fieldDescription={t('icon-description')}
                        error={errors.logoId}
                    >
                        <AppIconUploader
                            disabled={props.disabled}
                            logoUrl={props.logoUrl}
                            imageId={values.logoId}
                            onUploadChange={onUploaderChange}
                        />
                    </Field>
                    <ScreenshotsBlock
                        disabled={props.disabled}
                        fieldTitle={t('screenshots-title')}
                        fieldDescription={t('screenshots-description')}
                        items={values.screenshots ?? []}
                        updateScreenshots={onChange('screenshots')}
                    />
                </FormCard>
                <FormCard
                    cardKey="agreement"
                    cardTitle={t('agreement')}
                    handleOpenCard={handleMobileCardClick}
                    openCardName={openCardName}
                >
                    <Field fieldTitle={t('eula-title')} fieldDescription={t('eula-description')}>
                        <FileUploaderInput
                            disabled={props.disabled}
                            onUploadComplete={onChange('eulaAgreement') as (data?: unknown) => void}
                            uploadedData={values.eulaAgreement}
                        />
                    </Field>
                    <Field fieldTitle={t('private-policy-title')} fieldDescription={t('private-policy-description')}>
                        <FileUploaderInput
                            disabled={props.disabled}
                            onUploadComplete={onChange('privacyAgreement') as (data?: unknown) => void}
                            uploadedData={values.privacyAgreement}
                        />
                    </Field>
                </FormCard>
            </div>
        </ErrorBoundary>
    );
};

const InstallableAppForm = formContainer<OuterProps, InstallableAppFormSettings>({
    mapPropsToValues: (props) => mapInstallableAppSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToInstallableAppSettings(values, props.appStoreSnapshot);

            await updateInstallableAppDraft(props.app.id, props.app.channel, settings);

            addToast(props, { title: tGeneralSettings('changes-save-success'), theme: 'success' });
            if (props.refreshAppStoreSnapshot) {
                await props.refreshAppStoreSnapshot();
            }
        } catch (err) {
            // @ts-ignore
            await new Promise((resolve) => setErrors(mapApiErrorsToFormErrors(err.message), resolve));
            addToast(props, { title: tGeneralSettings('error'), theme: 'danger' });
            console.error(err);
            throw err;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.app.developerType);
        return {
            activationPhrases: (value: string[]) => ({
                activationPhrases: value.map((v) => validateActivationPhrase(v, validateAsAdmin, 'Активационное имя')),
            }),
            apkInfoWithTargets: (value: Partial<AppVersionInfoWithTargetPlatforms>) => {
                if (!value.versionName) {
                    return { apkInfoWithTargets: ApkErrors.NameError };
                }
                if (!value?.versionCode) {
                    return { apkInfoWithTargets: ApkErrors.VersionError };
                }
                if (value === undefined || value.appVersionId === undefined)
                    return { apkInfoWithTargets: ApkErrors.UploadApkError };
                if (__appConfig.user.isAdmin && (!value?.targetPlatforms || value.targetPlatforms.length === 0)) {
                    return { apkInfoWithTargets: ApkErrors.TargetPlatforms };
                }

                return { apkInfoWithTargets: '' };
            },
            packageName: (value: string) => {
                const re = /^([A-Za-z]{1}[A-Za-z\d_]*\.)+[A-Za-z][A-Za-z\d_]*$/;

                if (re.test(value)) {
                    return { packageName: '' };
                }

                return { packageName: t('package-name-error') };
            },
            ageRestriction: (value: AgeRestriction | undefined) => {
                if (value === undefined || props.appStoreSnapshot.ageRestrictions.indexOf(value) < 0) {
                    return { ageRestriction: tError('age-restriction') };
                }

                return { ageRestriction: '' };
            },
            description: (value: string) =>
                getStringFieldErrorMessage(
                    'description',
                    value,
                    tError('empty-description'),
                    lengthConfig['description'],
                    tError('too-long', { characters: lengthConfig['description'] }),
                ),
            developerName: (value: string) => {
                const validateDevName = validateDeveloperName(value, validateAsAdmin, t('developer-name'));
                return {
                    developerName:
                        validateDevName &&
                        tError('validation-developer-name', {
                            word: validateDevName.split('содержать ')[1],
                        }),
                };
            },
            name: (value: string) => ({
                name: value ? validateName(value, validateAsAdmin, 'Название') : tError('name-is-required'),
            }),
            noteForModerator: (value: string) =>
                getStringFieldErrorMessage(
                    'noteForModerator',
                    value,
                    '',
                    lengthConfig['noteForModerator'],
                    tError('too-long', { characters: lengthConfig['noteForModerator'] }),
                ),
            secondaryTitle: (value: string) =>
                getStringFieldErrorMessage(
                    'secondaryTitle',
                    value,
                    tError('empty-short-description'),
                    lengthConfig['secondaryTitle'],
                    tError('too-long', { characters: lengthConfig['secondaryTitle'] }),
                ),
            email: (value: string) => ({
                email: validateEmail(value) && tError('validation-email'),
            }),
        };
    },
})(InstallableAppSettingFields);

const InstallableAppWithToaster = withToaster<OuterProps & FormContainerProps>()(InstallableAppForm);

export default withAppStoreSnapshot(InstallableAppWithToaster);
